import { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, Chip, CircularProgress, Divider, IconButton, Alert,
} from '@mui/material';
import {
    Close as CloseIcon,
    BarChart as BarChartIcon,
    PeopleAlt as PeopleAltIcon,
    HourglassEmpty as HourglassEmptyIcon,
    Refresh as RefreshIcon,
    FileDownloadOutlined as FileDownloadIcon,
} from '@mui/icons-material';
import { useQueryResult } from '../../../../hooks/QueryBuilder/useQueryResult';
import { useQueryCount } from '../../../../hooks/QueryBuilder/useQueryCount';
import { useExtractPersonQuery } from '../../../../hooks/QueryBuilder';
import { useUser } from '../../../../context/UserContext';
import { toast } from 'react-toastify';
import NotificationService, { Notification } from '../../../../services/NotificationService';
import CountCards from './CountCards';
import PersonIdsTable from './PersonIdsTable';
import QueryExportDialog from './QueryExportDialog';

interface QueryResultDialogProps {
    open: boolean;
    onClose: () => void;
    queryId: string | number | null;
    description?: string;
}

function QueryResultDialog({
    open,
    onClose,
    queryId,
}: QueryResultDialogProps) {
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const { userInfos } = useUser();
    const { fetchQueryResult, description, loading, error, result, reset, markAsRunning } = useQueryResult();
    const { launchQueryCount, loading: relaunchingCount, error: relaunchError, reset: resetCount } = useQueryCount();
    const { extractPersonQuery, loading: exporting, error: exportError, reset: resetExport } = useExtractPersonQuery();

    useEffect(() => {
        if (open && queryId) fetchQueryResult(queryId);
        if (!open) {
            reset();
            resetCount();
            resetExport();
            setExportDialogOpen(false);
        }
    }, [open, queryId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Fallback fiable : quand le polling détecte COMPLETED, rafraîchit le panneau notifs ──
    useEffect(() => {
        const normalizedStatus = result?.status?.toUpperCase();
        const isCompleted = normalizedStatus === 'COMPLETED' || normalizedStatus === 'DONE';
        if (isCompleted && queryId) {
            NotificationService.refreshFromRest().catch(() => {});
        }
    }, [result?.status, queryId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Listener SSE : si une notif arrive pour ce queryId, rafraîchit immédiatement ──
    useEffect(() => {
        if (!open || !queryId) return;

        const handleNewNotification = (notification: Notification) => {
            const notifQueryId = notification.queryId ? String(notification.queryId) : null;
            const currentQueryId = String(queryId);
            if (!notifQueryId || notifQueryId !== currentQueryId) return;
            fetchQueryResult(queryId, { keepPreviousResult: true });
        };

        NotificationService.on('new-notification', handleNewNotification);
        return () => {
            NotificationService.removeListener('new-notification', handleNewNotification);
        };
    }, [open, queryId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open || !queryId) return;
        const normalizedStatus = result?.status?.toUpperCase();
        const isPendingOrRunning =
            normalizedStatus === 'PENDING' ||
            normalizedStatus === 'RUNNING' ||
            normalizedStatus === 'IN_PROGRESS';
        if (!isPendingOrRunning) return;

        const interval = setInterval(() => {
            fetchQueryResult(queryId, { keepPreviousResult: true });
        }, 5000);

        return () => clearInterval(interval);
    }, [open, queryId, result?.status]); // eslint-disable-line react-hooks/exhaustive-deps

    const normalizedStatus = result?.status?.toUpperCase();
    const isExpired = normalizedStatus?.includes('EXPIRED') || normalizedStatus?.includes('UNKNOWN');
    const isError = normalizedStatus === 'ERROR';
    const isCompleted = normalizedStatus === 'COMPLETED' || normalizedStatus === 'DONE';
    const isPending = normalizedStatus === 'PENDING' || normalizedStatus === 'RUNNING' || normalizedStatus === 'IN_PROGRESS';

    const countTotal = result?.count ?? result?.listCount?.reduce((acc, i) => acc + i.count, 0);
    const hasExportableResults = isCompleted && typeof countTotal === 'number' && countTotal > 0;
    const canRelaunchCount = !!queryId && !loading && (isExpired || isError);
    const canExport = !!queryId && !!result?.taskId && hasExportableResults;
    const isRelaunching = relaunchingCount;
    const displayError = error || relaunchError;
    const defaultExportEmail = userInfos?.email || '';

    const handleRelaunchCount = async () => {
        if (!queryId) return;

        const response = await launchQueryCount(queryId);
        if (response) {
            if (response.taskId) {
                markAsRunning(response.taskId);
            } else if (response.count !== undefined) {
                await fetchQueryResult(queryId);
            }
        }
    };

    const handleExportQuery = async ({ notify, canal }: { notify: string[]; canal: string }) => {
        if (!queryId || !result?.taskId) return;

        const toastId = toast.loading("Demande d'export en cours...");
        try {
            await extractPersonQuery({
                taskId: result.taskId,
                queryId,
                notify,
                canal,
            });

            setExportDialogOpen(false);
            toast.update(toastId, {
                render: "Demande d'export envoyée. Vous recevrez le fichier par email.",
                type: 'success',
                isLoading: false,
                autoClose: 5000,
            });
        } catch (err) {
            toast.update(toastId, {
                render: err instanceof Error ? err.message : exportError || "Erreur lors de la demande d'export",
                type: 'error',
                isLoading: false,
                autoClose: 5000,
            });
        }
    };

    return (
        <>
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChartIcon sx={{ color: '#1976d2' }} />
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            Résultats de la requête
                        </Typography>
                        {description && (
                            <Typography variant="caption" sx={{ color: '#6c757d' }}>
                                {description}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2, minHeight: 200 }}>
                {loading && !result && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 2 }}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                            Récupération des résultats…
                        </Typography>
                    </Box>
                )}

                {displayError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {displayError}
                    </Alert>
                )}

                {result && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            {countTotal !== undefined && isCompleted && (
                                <Chip
                                    label={`${countTotal.toLocaleString('fr-FR')} client${countTotal > 1 ? 's' : ''}`}
                                    size="small"
                                    icon={<PeopleAltIcon />}
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                />
                            )}
                        </Box>

                        {isExpired && (
                            <Alert severity="warning" icon={<HourglassEmptyIcon />}>
                                Aucun résultat disponible pour cette requête. Le calcul n'a pas encore été exécuté ou a expiré.
                                Vous pouvez relancer le comptage pour cette requête.
                            </Alert>
                        )}

                        {isError && (
                            <Alert severity="error">
                                <strong>Le calcul a échoué.</strong>
                                {result.message?.includes('bad SQL grammar') ? (
                                    <> La requête sauvegardée génère un SQL invalide. Modifiez les critères puis relancez le comptage.
                                    {result.message && (
                                        <Box component="pre" sx={{ mt: 1, fontSize: '0.7rem', whiteSpace: 'pre-wrap', opacity: 0.7 }}>
                                            {result.message}
                                        </Box>
                                    )}
                                    </>
                                ) : (
                                    <> {result.message || 'Une erreur est survenue lors du traitement de la requête.'}</>
                                )}
                            </Alert>
                        )}

                        {isPending && (
                            <Box
                                sx={{
                                    p: 2.5,
                                    border: '1px solid #90caf9',
                                    borderRadius: 1,
                                    bgcolor: '#e3f2fd',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <CircularProgress size={22} thickness={5} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d47a1' }}>
                                            Comptage en cours
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#1565c0' }}>
                                            La requête est en cours de traitement. Cette fenêtre se met à jour automatiquement toutes les 5 secondes.
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {isCompleted && result.listCount && result.listCount.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#495057' }}>
                                    Répartition par canal
                                </Typography>
                                <CountCards listCount={result.listCount} />
                            </Box>
                        )}

                        {isCompleted && !result.listCount && result.count !== undefined && (
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    p: 3,
                                    border: '2px solid #1976d2',
                                    borderRadius: 2,
                                    bgcolor: '#e3f2fd',
                                }}
                            >
                                <PeopleAltIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                                <Typography variant="h3" sx={{ fontWeight: 800, color: '#1976d2' }}>
                                    {result.count.toLocaleString('fr-FR')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    clients dans la segmentation
                                </Typography>
                            </Box>
                        )}

                        {isCompleted && result.personIds && result.personIds.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#495057' }}>
                                    Liste des IDs Person ({result.personIds.length.toLocaleString('fr-FR')} résultats)
                                </Typography>
                                <PersonIdsTable personIds={result.personIds} />
                            </Box>
                        )}


                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, justifyContent: canRelaunchCount || canExport ? 'space-between' : 'flex-end' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                {canRelaunchCount && (
                    <Button
                        onClick={handleRelaunchCount}
                        variant="contained"
                        size="small"
                        startIcon={isRelaunching ? <CircularProgress color="inherit" size={16} /> : <RefreshIcon />}
                        disabled={isRelaunching}
                    >
                        {isRelaunching ? 'Relance en cours...' : 'Relancer le comptage'}
                    </Button>
                )}
                {canExport && (
                    <Button
                        onClick={() => setExportDialogOpen(true)}
                        variant="contained"
                        size="small"
                        startIcon={<FileDownloadIcon />}
                    >
                        Exporter
                    </Button>
                )}
                </Box>
                <Button onClick={onClose} variant="outlined" size="small">
                    Fermer
                </Button>
            </DialogActions>
        </Dialog>

        <QueryExportDialog
            open={exportDialogOpen}
            defaultEmail={defaultExportEmail}
            loading={exporting}
            error={exportError}
            onClose={() => setExportDialogOpen(false)}
            onConfirm={handleExportQuery}
        />
        </>
    );
};

export default QueryResultDialog;
